import { ButtonPermissioned } from "components/ButtonPermissioned/ButtonPermissioned";
import { useState } from "react";
import { getValidationErrors } from "../cell-renderers/CellRendererBase.helpers";
import { useDataTableContext } from "../store/DataTableProvider";
import { ModalConfirmStartTasks } from "../modals/ModalConfirmStartTasks";
import { useExecuteAnalysisTableRows } from "./DataTableQuickActionBar/useExecuteAnalysisTableRows";
import { GridApi } from "ag-grid-community";
import { DataTableRowData } from "./DataTable";

export interface ButtonStartTasksProps {
  selectedRowIds: string[];
  gridApi: GridApi<DataTableRowData> | undefined;
  tableId: string;
}
export const ButtonStartTasks = ({
  selectedRowIds,
  gridApi,
  tableId,
}: ButtonStartTasksProps) => {
  const validRowIds = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === tableId);

    if (table === undefined) {
      return [];
    }

    return (table.rows ?? [])
      .filter((row) => {
        return (
          row.task === null &&
          row.cells.every((cell, colIdx) => {
            const column = table.columns[colIdx];
            const errors = getValidationErrors({
              tableKind: table.kind,
              columnDefinition: column.definition,
              isColumnRequired: column.required,
              value: cell.value,
            });
            const blockingErrors = errors.filter((e) => e.isBlocking);
            return blockingErrors.length === 0;
          })
        );
      })
      .map((row) => row.id);
  });
  const anyRowSelected = selectedRowIds.length > 0;
  const validRowsToSubmit = anyRowSelected
    ? validRowIds.filter((rowId) => selectedRowIds.includes(rowId))
    : validRowIds;
  const executeAnalysisTableRows = useExecuteAnalysisTableRows(gridApi);
  const syncStatus = useDataTableContext((s) => s.syncStatus);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const startTasks = async () => {
    setIsLoading(true);
    await executeAnalysisTableRows(validRowsToSubmit, tableId);
    setIsLoading(false);
  };

  const tooltipMessage = (() => {
    if (isLoading) {
      return "Waiting for tasks to submit";
    }

    if (validRowsToSubmit.length === 0) {
      return anyRowSelected
        ? "No valid rows selected"
        : "You have no valid unexecuted rows";
    }
    if (syncStatus.status !== "idle") {
      return "Wait for table to save";
    }
  })();

  const onCancel = () => {
    setShowConfirmModal(false);
  };
  const onConfirm = () => {
    void startTasks();
    setShowConfirmModal(false);
  };

  return (
    <>
      <ButtonPermissioned
        style={{ minWidth: "170px" }} // to prevent the button from resizing when the text changes
        fill
        requiredPermission="edit"
        defaultTooltip={tooltipMessage}
        isDisabled={
          isLoading ||
          validRowsToSubmit.length === 0 ||
          syncStatus.status !== "idle"
        }
        iconType={"playFilled"}
        isLoading={isLoading}
        onClick={() => setShowConfirmModal(true)}
        size="s"
      >
        {!anyRowSelected
          ? `Start All Tasks (${validRowsToSubmit.length})`
          : `Start Tasks (${validRowsToSubmit.length}/${selectedRowIds.length})`}
      </ButtonPermissioned>
      {showConfirmModal && (
        <ModalConfirmStartTasks
          onCancel={onCancel}
          onConfirm={onConfirm}
          numberOfTasks={validRowsToSubmit.length}
        />
      )}
    </>
  );
};
